import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  getProducts,
  getProductsAdminFacets,
  getCategories,
  getBrands,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  exportProductsExcel,
  downloadSampleProductsExcel,
  importProductsExcel,
} from '../../lib/api';
import { slugify } from '../../lib/utils';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, Image, Upload, X, Download, FileSpreadsheet, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const PRODUCT_TYPES = ['EDP', 'EDT', 'Parfum', 'Deo', 'Body Mist', 'Attar', 'Gift Set', 'Other'];
const PAGE_SIZE = 25;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalProductsInDb, setTotalProductsInDb] = useState(0);
  const [adminFacetTypes, setAdminFacetTypes] = useState([]);
  const [adminFacetVolumes, setAdminFacetVolumes] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listVersion, setListVersion] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category_id: '',
    brand_id: '',
    product_type: '',
    volume_ml: '',
    availability: 'In Stock',
    moq: '',
    short_description: '',
    full_description: '',
    notes_top: '',
    notes_middle: '',
    notes_base: '',
    images: [],
    featured: false,
  });
  const [imageInput, setImageInput] = useState('');
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const FILTER_ALL = 'all';
  const [productSearch, setProductSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState(FILTER_ALL);
  const [filterBrand, setFilterBrand] = useState(FILTER_ALL);
  const [filterType, setFilterType] = useState(FILTER_ALL);
  const [filterVolume, setFilterVolume] = useState(FILTER_ALL);
  const [filterStatus, setFilterStatus] = useState(FILTER_ALL);

  const triggerDownloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const xlsxMime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const handleExportExcel = async () => {
    try {
      const res = await exportProductsExcel();
      const ct = res.headers['content-type'] || '';
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        const j = JSON.parse(text);
        toast.error(j.detail || 'Export failed');
        return;
      }
      triggerDownloadBlob(new Blob([res.data], { type: xlsxMime }), 'products-export.xlsx');
      toast.success('Products exported');
    } catch (error) {
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          toast.error(j.detail || 'Export failed');
        } catch {
          toast.error('Export failed');
        }
      } else {
        toast.error('Export failed');
      }
    }
  };

  const handleSampleExcel = async () => {
    try {
      const res = await downloadSampleProductsExcel();
      const ct = res.headers['content-type'] || '';
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        const j = JSON.parse(text);
        toast.error(j.detail || 'Could not download sample');
        return;
      }
      triggerDownloadBlob(new Blob([res.data], { type: xlsxMime }), 'products-sample.xlsx');
      toast.success('Sample Excel downloaded');
    } catch (error) {
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          toast.error(j.detail || 'Could not download sample');
        } catch {
          toast.error('Could not download sample');
        }
      } else {
        toast.error('Could not download sample');
      }
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      toast.error('Please choose an Excel file (.xlsx)');
      return;
    }
    setImporting(true);
    try {
      const res = await importProductsExcel(file);
      const { created, updated, errors, categories_created: newCats, brands_created: newBrands } = res.data;
      const parts = [];
      if (created) parts.push(`${created} product(s) created`);
      if (updated) parts.push(`${updated} updated`);
      if (newCats) parts.push(`${newCats} new categor${newCats === 1 ? 'y' : 'ies'}`);
      if (newBrands) parts.push(`${newBrands} new brand(s)`);
      toast.success(parts.length ? parts.join(' · ') : 'Import finished');
      if (errors?.length) {
        const preview = errors.slice(0, 5).map((err) => `Row ${err.row}: ${err.message}`).join('; ');
        toast.message(`Some rows failed (${errors.length})`, { description: preview });
      }
      await refreshCatalogAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const loadCatalogMeta = async () => {
    const [catsRes, brandsRes, facRes] = await Promise.all([
      getCategories(),
      getBrands(),
      getProductsAdminFacets(),
    ]);
    setCategories(catsRes.data);
    setBrands(brandsRes.data);
    setAdminFacetTypes(facRes.data.product_types || []);
    setAdminFacetVolumes(facRes.data.volumes || []);
    setTotalProductsInDb(facRes.data.total_products ?? 0);
  };

  const refreshCatalogAfterMutation = async () => {
    try {
      await loadCatalogMeta();
    } catch {
      toast.error('Failed to refresh catalog');
    }
    setListVersion((v) => v + 1);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMetaLoading(true);
      try {
        await loadCatalogMeta();
      } catch {
        if (!cancelled) toast.error('Failed to fetch catalog metadata');
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(productSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterCategory, filterBrand, filterType, filterVolume, filterStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProductsLoading(true);
      try {
        const params = {
          limit: PAGE_SIZE,
          skip: (page - 1) * PAGE_SIZE,
          with_total: true,
        };
        const q = debouncedSearch.trim();
        if (q) params.search = q;
        if (filterCategory !== FILTER_ALL) params.category_id = filterCategory;
        if (filterBrand !== FILTER_ALL) params.brand_id = filterBrand;
        if (filterType !== FILTER_ALL) params.product_type = filterType;
        if (filterVolume !== FILTER_ALL) params.volume_ml = filterVolume;
        if (filterStatus !== FILTER_ALL) params.availability = filterStatus;

        const res = await getProducts(params);
        if (cancelled) return;
        const payload = res.data;
        const items = Array.isArray(payload) ? payload : (payload.items ?? []);
        const total = Array.isArray(payload) ? payload.length : Number(payload.total ?? 0);
        setProducts(items);
        setTotalCount(total);
        const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
        if (total > 0 && page > maxPage) setPage(maxPage);
      } catch {
        if (!cancelled) {
          toast.error('Failed to fetch products');
          setProducts([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    debouncedSearch,
    filterCategory,
    filterBrand,
    filterType,
    filterVolume,
    filterStatus,
    listVersion,
  ]);

  const brandsForCategory = useMemo(() => {
    if (!formData.category_id) return [];
    return brands.filter((b) => b.category_id === formData.category_id);
  }, [brands, formData.category_id]);

  const distinctTypesForFilter = useMemo(() => {
    const set = new Set(PRODUCT_TYPES);
    adminFacetTypes.forEach((t) => set.add(t));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [adminFacetTypes]);

  const brandsForTableFilter = useMemo(() => {
    if (filterCategory === FILTER_ALL) return brands;
    return brands.filter((b) => b.category_id === filterCategory);
  }, [brands, filterCategory]);

  useEffect(() => {
    setFilterBrand(FILTER_ALL);
  }, [filterCategory]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);

  const clearProductFilters = () => {
    setProductSearch('');
    setFilterCategory(FILTER_ALL);
    setFilterBrand(FILTER_ALL);
    setFilterType(FILTER_ALL);
    setFilterVolume(FILTER_ALL);
    setFilterStatus(FILTER_ALL);
  };

  const hasActiveProductFilters =
    productSearch.trim() !== '' ||
    filterCategory !== FILTER_ALL ||
    filterBrand !== FILTER_ALL ||
    filterType !== FILTER_ALL ||
    filterVolume !== FILTER_ALL ||
    filterStatus !== FILTER_ALL;

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({ ...formData, name, slug: slugify(name) });
  };

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setFormData({ ...formData, images: [...formData.images, imageInput.trim()] });
      setImageInput('');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const res = await uploadImage(file, 'product');
      setFormData({ ...formData, images: [...formData.images, res.data.url] });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id || !formData.brand_id) {
      toast.error('Please select category and brand');
      return;
    }
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      resetForm();
      await refreshCatalogAfterMutation();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      category_id: product.category_id,
      brand_id: product.brand_id,
      product_type: product.product_type || '',
      volume_ml: product.volume_ml || '',
      availability: product.availability || 'In Stock',
      moq: product.moq || '',
      short_description: product.short_description || '',
      full_description: product.full_description || '',
      notes_top: product.notes_top || '',
      notes_middle: product.notes_middle || '',
      notes_base: product.notes_base || '',
      images: product.images || [],
      featured: product.featured || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(id);
      toast.success('Product deleted successfully');
      await refreshCatalogAfterMutation();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      slug: '',
      category_id: '',
      brand_id: '',
      product_type: '',
      volume_ml: '',
      availability: 'In Stock',
      moq: '',
      short_description: '',
      full_description: '',
      notes_top: '',
      notes_middle: '',
      notes_base: '',
      images: [],
      featured: false,
    });
    setImageInput('');
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '-';

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-serif text-[#0B1F3B]">Products</h1>
          <p className="text-gray-500">
            {metaLoading
              ? 'Loading catalog metadata…'
              : `Manage product catalog (${
                  hasActiveProductFilters
                    ? `${totalCount} matching · page ${page} of ${totalPages}`
                    : `${totalProductsInDb} products · page ${page} of ${totalPages}`
                })`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handleSampleExcel} data-testid="products-sample-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Sample Excel
          </Button>
          <Button type="button" variant="outline" onClick={handleExportExcel} data-testid="products-export-excel-btn">
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            type="button"
            variant="outline"
            disabled={importing}
            onClick={() => importInputRef.current?.click()}
            data-testid="products-import-excel-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {importing ? 'Importing…' : 'Import Excel'}
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {!metaLoading && totalProductsInDb > 0 && (
        <div className="bg-white border border-gray-100 p-4 space-y-4" data-testid="admin-products-filters">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-3 xl:col-span-2">
              <Label htmlFor="product-search" className="text-xs text-gray-500 uppercase tracking-wide">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                <Input
                  id="product-search"
                  className="pl-9"
                  placeholder="Name, slug, description…"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  data-testid="admin-products-search"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="admin-products-filter-category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Brand</Label>
              <Select
                key={filterCategory}
                value={filterBrand}
                onValueChange={setFilterBrand}
                disabled={filterCategory === FILTER_ALL}
              >
                <SelectTrigger data-testid="admin-products-filter-brand">
                  <SelectValue
                    placeholder={
                      filterCategory === FILTER_ALL
                        ? 'Select a category first'
                        : 'All brands'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All brands</SelectItem>
                  {brandsForTableFilter.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="admin-products-filter-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All types</SelectItem>
                  {distinctTypesForFilter.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Volume</Label>
              <Select value={filterVolume} onValueChange={setFilterVolume}>
                <SelectTrigger data-testid="admin-products-filter-volume">
                  <SelectValue placeholder="All volumes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All volumes</SelectItem>
                  {adminFacetVolumes.map((vol) => (
                    <SelectItem key={vol} value={vol}>{vol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="admin-products-filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All statuses</SelectItem>
                  <SelectItem value="In Stock">In Stock</SelectItem>
                  <SelectItem value="On Request">On Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveProductFilters && (
            <div className="flex justify-end border-t border-gray-100 pt-3">
              <Button type="button" variant="outline" size="sm" onClick={clearProductFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-gray-100 overflow-hidden">
        {metaLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : totalProductsInDb > 0 ? (
          productsLoading ? (
            <div className="p-8 text-center text-gray-500">Loading products…</div>
          ) : totalCount > 0 ? (
          <>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Image</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Brand</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Volume</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-4 py-3">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 flex items-center justify-center rounded">
                          <Image className="h-4 w-4 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{product.name}</div>
                      {product.featured && (
                        <span className="text-xs text-[#D4AF37]">Featured</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getCategoryName(product.category_id)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getBrandName(product.brand_id)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{product.product_type || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{product.volume_ml || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        product.availability === 'In Stock' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {product.availability}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleEdit(product)} className="p-2 text-gray-400 hover:text-[#0B1F3B]">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(product.id)} className="p-2 text-gray-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing{' '}
              {totalCount === 0
                ? 0
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)}`}{' '}
              of {totalCount}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || productsLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span tabIndex={0}>
                Page {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || productsLoading}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          </>
          ) : (
            <div className="p-12 text-center text-gray-500 space-y-3">
              <p>No products match your search or filters.</p>
              <Button type="button" variant="outline" size="sm" onClick={clearProductFilters}>
                Clear filters
              </Button>
            </div>
          )
        ) : (
          <div className="p-12 text-center text-gray-500">
            <p>No products yet. Click &quot;Add Product&quot; to create one.</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={formData.name} onChange={handleNameChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
              </div>
            </div>

            {/* Category & Brand */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category_id || undefined}
                  onValueChange={(val) =>
                    setFormData({ ...formData, category_id: val, brand_id: '' })
                  }
                >
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Brand *</Label>
                <Select
                  key={formData.category_id || 'no-category'}
                  value={formData.brand_id || undefined}
                  onValueChange={(val) => setFormData({ ...formData, brand_id: val })}
                  disabled={!formData.category_id}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        formData.category_id
                          ? brandsForCategory.length
                            ? 'Select brand'
                            : 'No brands in this category'
                          : 'Select a category first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {brandsForCategory.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type, Volume, Availability */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={formData.product_type} onValueChange={(val) => setFormData({ ...formData, product_type: val })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="volume_ml">Volume</Label>
                <Input id="volume_ml" value={formData.volume_ml} onChange={(e) => setFormData({ ...formData, volume_ml: e.target.value })} placeholder="e.g., 100ml" />
              </div>
              <div className="space-y-2">
                <Label>Availability</Label>
                <Select value={formData.availability} onValueChange={(val) => setFormData({ ...formData, availability: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Stock">In Stock</SelectItem>
                    <SelectItem value="On Request">On Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* MOQ */}
            <div className="space-y-2">
              <Label htmlFor="moq">Minimum Order Quantity (MOQ)</Label>
              <Input id="moq" value={formData.moq} onChange={(e) => setFormData({ ...formData, moq: e.target.value })} placeholder="e.g., 6 units" />
            </div>

            {/* Descriptions */}
            <div className="space-y-2">
              <Label htmlFor="short_description">Short Description</Label>
              <Textarea id="short_description" value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} rows={2} placeholder="Brief product summary..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="full_description">Full Description</Label>
              <Textarea id="full_description" value={formData.full_description} onChange={(e) => setFormData({ ...formData, full_description: e.target.value })} rows={3} placeholder="Detailed product description..." />
            </div>

            {/* Fragrance Notes */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Fragrance Notes</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div className="space-y-2">
                  <Label htmlFor="notes_top" className="text-sm text-gray-500">Top Notes</Label>
                  <Input id="notes_top" value={formData.notes_top} onChange={(e) => setFormData({ ...formData, notes_top: e.target.value })} placeholder="Bergamot, Lemon..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes_middle" className="text-sm text-gray-500">Middle Notes</Label>
                  <Input id="notes_middle" value={formData.notes_middle} onChange={(e) => setFormData({ ...formData, notes_middle: e.target.value })} placeholder="Rose, Jasmine..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes_base" className="text-sm text-gray-500">Base Notes</Label>
                  <Input id="notes_base" value={formData.notes_base} onChange={(e) => setFormData({ ...formData, notes_base: e.target.value })} placeholder="Musk, Amber..." />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2 border-t pt-4">
              <Label>Product Images</Label>
              <div className="flex gap-2">
                <Input
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  placeholder="Paste image URL"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddImage}>Add URL</Button>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="h-4 w-4 mr-1" />{uploading ? '...' : 'Upload'}</span>
                  </Button>
                </label>
              </div>
              {formData.images.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-20 h-20 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Featured */}
            <div className="flex items-center gap-2 border-t pt-4">
              <Checkbox
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
              <Label htmlFor="featured" className="cursor-pointer">Featured Product (shown on homepage)</Label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1">{editingProduct ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
